import { GitBranch, Target, User, Waypoints, FileText, ListChecks, Trash2 } from 'lucide-react';
import { Badge, Card, CardBody } from '../../ui';
import { formatDateTime } from '../../features/changes/changeModel';
import type { EngineeringChangeSummary } from '../../features/changes/types';

export interface ChangeCardProps {
  change: EngineeringChangeSummary;
  /** Resolved linked goal title, if any. */
  goalTitle?: string;
  /** Resolved linked workstream title, if any. */
  workstreamTitle?: string;
  onOpenGoal?: (goalId: string) => void;
  onOpenWorkstream?: (workstreamId: string) => void;
  onDelete?: (change: EngineeringChangeSummary) => void;
}

/** A labeled context block, prominent so why/impact lead the card. */
function Context({ label, value, tone }: { label: string; value: string; tone: string }) {
  if (!value.trim()) return null;
  return (
    <div className={`rounded-lg border px-3 py-2 ${tone}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
        {label}
      </p>
      <p className="mt-0.5 whitespace-pre-wrap text-sm">{value}</p>
    </div>
  );
}

/**
 * A single Change Feed entry. Deliberately leads with the engineering CONTEXT
 * (why + impact) rather than the raw "what changed", so the feed rewards
 * context over volume. Related goal / workstream / story links are secondary.
 */
export function ChangeCard({
  change,
  goalTitle,
  workstreamTitle,
  onOpenGoal,
  onOpenWorkstream,
  onDelete,
}: ChangeCardProps) {
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-zinc-100">{change.title}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
              <span className="inline-flex items-center gap-1">
                <User className="h-3 w-3" aria-hidden="true" />
                {change.engineer || '—'}
              </span>
              <span>{formatDateTime(change.createdAt)}</span>
            </div>
          </div>
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(change)}
              aria-label="Delete change"
              className="shrink-0 rounded-md p-1 text-zinc-500 transition-colors hover:bg-surface-3 hover:text-red-300"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Context leads. */}
        <Context
          label="Why"
          value={change.why}
          tone="border-accent/30 bg-accent-soft/20 text-zinc-200"
        />
        <Context
          label="Impact"
          value={change.impact}
          tone="border-sky-500/30 bg-sky-500/10 text-sky-100"
        />

        {change.changed.trim() && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
              What changed
            </p>
            <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-300">
              {change.changed}
            </p>
          </div>
        )}

        {/* Secondary: related links. */}
        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
          {change.goalId && (
            <button
              type="button"
              onClick={() => change.goalId && onOpenGoal?.(change.goalId)}
              disabled={!onOpenGoal}
            >
              <Badge tone="accent">
                <Target className="h-3 w-3" /> {goalTitle ?? 'Goal'}
              </Badge>
            </button>
          )}
          {change.workstreamId && (
            <button
              type="button"
              onClick={() =>
                change.workstreamId && onOpenWorkstream?.(change.workstreamId)
              }
              disabled={!onOpenWorkstream}
            >
              <Badge tone="info">
                <Waypoints className="h-3 w-3" /> {workstreamTitle ?? 'Workstream'}
              </Badge>
            </button>
          )}
          {change.relatedStoryCount > 0 && (
            <Badge tone="neutral">
              <ListChecks className="h-3 w-3" /> {change.relatedStoryCount}{' '}
              {change.relatedStoryCount === 1 ? 'story' : 'stories'}
            </Badge>
          )}
          {change.relatedDocCount > 0 && (
            <Badge tone="neutral">
              <FileText className="h-3 w-3" /> {change.relatedDocCount}{' '}
              {change.relatedDocCount === 1 ? 'doc' : 'docs'}
            </Badge>
          )}
          {!change.goalId &&
            !change.workstreamId &&
            change.relatedStoryCount === 0 &&
            change.relatedDocCount === 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-zinc-600">
                <GitBranch className="h-3 w-3" aria-hidden="true" /> no links
              </span>
            )}
        </div>
      </CardBody>
    </Card>
  );
}

export default ChangeCard;
